import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Download, Plus, Trash2, TrendingUp, CalendarDays, Coins, Receipt } from "lucide-react";

const TYPE_LABEL = { deposit: "Záloha", full: "Celá částka", voucher: "Poukaz", manual: "Ruční", platba: "Platba", rezervace: "Rezervace" };

function fmtDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("cs-CZ", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch (e) { return iso.slice(0, 16); }
}
const CZK = (n) => `${Math.round(n).toLocaleString("cs-CZ")} Kč`;

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="border border-white/10 bg-[#0b0a09] p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-widest mb-2"><Icon className="w-4 h-4 text-nv" /> {label}</div>
      <p className="font-head text-2xl lg:text-3xl text-neutral-100">{value}</p>
    </div>
  );
}

export default function AdminProfits() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mp, setMp] = useState({ amount: "", label: "Doplatek na místě", email: "", created_at: "" });
  const [adding, setAdding] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/profits").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const addManual = async () => {
    if (!mp.amount || Number(mp.amount) <= 0) { toast.error("Zadejte částku."); return; }
    setAdding(true);
    try {
      await api.post("/admin/profits/manual", { ...mp, amount: Number(mp.amount) });
      toast.success("Platba přidána.");
      setMp({ amount: "", label: "Doplatek na místě", email: "", created_at: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Nepodařilo se."); }
    finally { setAdding(false); }
  };

  const delManual = async (id) => {
    if (!window.confirm("Smazat tuto ruční platbu?")) return;
    try { await api.delete(`/admin/profits/manual/${id}`); toast.success("Smazáno."); load(); } catch (e) { toast.error("Nepodařilo se."); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/profits/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `zisky-hubert-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Export stažen.");
    } catch (e) { toast.error("Export selhal."); }
  };

  if (loading) return <div className="p-4 text-neutral-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítám…</div>;
  if (!data) return <p className="text-neutral-600 text-sm">Data se nepodařilo načíst.</p>;

  return (
    <div className="space-y-6" data-testid="admin-profits">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-head uppercase text-neutral-200 tracking-wide flex items-center gap-2"><Coins className="w-5 h-5 text-nv" /> Přehled zisků</h3>
        <button onClick={exportCsv} data-testid="export-profits-btn" className="hud-btn vhs-btn flex items-center gap-2 px-4 py-2 text-sm font-head"><Download className="w-4 h-4" /> Stáhnout vše (CSV)</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="Celkem" value={CZK(data.total)} testid="profit-total" />
        <StatCard icon={CalendarDays} label="Tento měsíc" value={CZK(data.this_month)} testid="profit-month" />
        <StatCard icon={CalendarDays} label="Tento rok" value={CZK(data.this_year)} testid="profit-year" />
        <StatCard icon={Receipt} label="Počet transakcí" value={data.count} testid="profit-count" />
      </div>

      {/* manual payment */}
      <div className="border border-white/10 bg-[#0b0a09] p-4" data-testid="manual-payment-form">
        <p className="font-head text-sm text-nv tracking-widest uppercase mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> Přidat platbu ručně</p>
        <div className="grid sm:grid-cols-5 gap-2 items-end">
          <div><Label className="text-xs text-neutral-400">Částka (Kč)</Label><Input type="number" data-testid="manual-amount" value={mp.amount} onChange={(e) => setMp({ ...mp, amount: e.target.value })} className="rounded-none bg-black border-white/15 text-sm mt-1" /></div>
          <div><Label className="text-xs text-neutral-400">Popis</Label><Input data-testid="manual-label" value={mp.label} onChange={(e) => setMp({ ...mp, label: e.target.value })} className="rounded-none bg-black border-white/15 text-sm mt-1" /></div>
          <div><Label className="text-xs text-neutral-400">E-mail (nepovinné)</Label><Input data-testid="manual-email" value={mp.email} onChange={(e) => setMp({ ...mp, email: e.target.value })} className="rounded-none bg-black border-white/15 text-sm mt-1" /></div>
          <div><Label className="text-xs text-neutral-400">Datum (nepovinné)</Label><Input type="date" data-testid="manual-date" value={mp.created_at} onChange={(e) => setMp({ ...mp, created_at: e.target.value })} className="rounded-none bg-black border-white/15 text-sm mt-1" /></div>
          <button onClick={addManual} disabled={adding} data-testid="add-manual-btn" className="hud-btn vhs-btn px-3 py-2 text-sm font-head">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Přidat"}</button>
        </div>
        <p className="text-[11px] text-neutral-500 mt-2">Např. doplatek, který zákazník uhradil na místě po zaplacené záloze online.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* transactions */}
        <div className="lg:col-span-2 border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0d0d0d] text-neutral-500 text-xs uppercase tracking-widest">
              <tr><th className="p-3 text-left">Datum</th><th className="p-3 text-left">Typ</th><th className="p-3 text-left">Popis</th><th className="p-3 text-right">Částka</th><th className="p-3"></th></tr>
            </thead>
            <tbody data-testid="transactions-table">
              {data.transactions.map((t) => (
                <tr key={t.id} className="border-t border-white/5" data-testid={`txn-${t.id}`}>
                  <td className="p-3 text-neutral-400 whitespace-nowrap">{fmtDT(t.created_at)}</td>
                  <td className="p-3"><span className="text-[10px] px-2 py-0.5 border border-white/15 text-neutral-300 uppercase tracking-widest">{TYPE_LABEL[t.type] || t.type}</span></td>
                  <td className="p-3 text-neutral-300">{t.label}{t.email && <span className="block text-xs text-neutral-500">{t.email}</span>}</td>
                  <td className="p-3 text-right text-nv font-head">{CZK(t.amount)}</td>
                  <td className="p-3 text-right">{t.source === "manual" && <button onClick={() => delManual(t.id)} data-testid={`del-manual-${t.id}`} className="text-red-400 hover:bg-red-950/30 p-1.5 border border-red-900/40"><Trash2 className="w-4 h-4" /></button>}</td>
                </tr>
              ))}
              {data.transactions.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-neutral-600 text-xs">Zatím žádné transakce.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* breakdowns */}
        <div className="space-y-4">
          <div className="border border-white/10 bg-[#0b0a09] p-4" data-testid="by-month">
            <p className="font-head text-sm text-nv tracking-widest uppercase mb-3">Po měsících</p>
            <div className="space-y-2">
              {data.by_month.map((m) => (
                <div key={m.month} className="flex items-center justify-between text-sm"><span className="text-neutral-400">{m.month}</span><span className="text-neutral-200">{CZK(m.total)}</span></div>
              ))}
              {data.by_month.length === 0 && <p className="text-xs text-neutral-600">—</p>}
            </div>
          </div>
          <div className="border border-white/10 bg-[#0b0a09] p-4" data-testid="by-year">
            <p className="font-head text-sm text-nv tracking-widest uppercase mb-3">Po letech</p>
            <div className="space-y-2">
              {data.by_year.map((y) => (
                <div key={y.year} className="flex items-center justify-between text-sm"><span className="text-neutral-400">{y.year}</span><span className="text-nv font-head">{CZK(y.total)}</span></div>
              ))}
              {data.by_year.length === 0 && <p className="text-xs text-neutral-600">—</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
